import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../services/api";
import Button from "../common/Button";
import "./ProcedureStepEditor.css";

interface StepRow {
  label: string;
  chart_mutation: string;
}

interface Procedure {
  name: string;
  default_steps?: (string | StepRow)[];
}

interface ProcedureStepEditorProps {
  procedureId: number | string | null;
  onClose?: () => void;
}

export default function ProcedureStepEditor({ procedureId, onClose }: ProcedureStepEditorProps) {
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [procedure, setProcedure] = useState<Procedure | null>(null);

  useEffect(() => {
    if (!procedureId) return;

    const load = async () => {
      setLoading(true);
      try {
        const proc = await apiGet(`/api/admin/procedures/${procedureId}`) as Procedure;
        setProcedure(proc);

        let procSteps = proc.default_steps || [];
        if (procSteps.length > 0 && typeof procSteps[0] === "string") {
          procSteps = (procSteps as string[]).map(label => ({ label, chart_mutation: "NONE" }));
        }

        setSteps(procSteps as StepRow[]);
      } catch (e) {
        console.error("Failed to load procedure", e);
        alert("Failed to load procedure");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [procedureId]);

  const updateStep = (i: number, field: keyof StepRow, value: string) => {
    const copy = [...steps];
    copy[i] = { ...copy[i], [field]: value };
    setSteps(copy);
  };

  const addStep = () => {
    setSteps([...steps, { label: "", chart_mutation: "NONE" }]);
  };

  const removeStep = (i: number) => {
    const copy = [...steps];
    copy.splice(i, 1);
    setSteps(copy);
  };

  const moveStep = (i: number, direction: "up" | "down") => {
    if (direction === "up" && i === 0) return;
    if (direction === "down" && i === steps.length - 1) return;

    const copy = [...steps];
    const targetIndex = direction === "up" ? i - 1 : i + 1;
    [copy[i], copy[targetIndex]] = [copy[targetIndex], copy[i]];
    setSteps(copy);
  };

  const mutationPreview = (code: string) => {
    const map: Record<string, string> = {
      NONE: "No chart change",
      FILLING: "Filling",
      CROWN: "Crown",
      CROWN_PLACED: "Crown",
      RCT_OPEN: "RCT Open",
      RCT_DONE: "RCT Done",
      RCT_CROWN: "RCT Crown",
      EXTRACT: "Missing/Extracted",
      EXTRACTION: "Missing/Extracted",
      IMPLANT_PLACED: "Implant",
      IMPLANT_RESTORED: "Crown",
    };
    return map[code] || code || "No chart change";
  };

  const save = async () => {
    if (!procedureId) return;

    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].label?.trim()) {
        alert(`Step ${i + 1} label is required`);
        return;
      }
    }

    try {
      await apiPost(`/api/admin/procedures/${procedureId}/steps`, {
        default_steps: steps
      });
      alert("✅ Steps saved successfully");
      if (onClose) onClose();
    } catch (e: unknown) {
      console.error("Failed to save steps", e);
      alert("Failed to save steps: " + ((e as Error)?.message || "Unknown error"));
    }
  };

  if (loading) {
    return (
      <div className="step-editor">
        <div className="step-editor-loading">Loading...</div>
      </div>
    );
  }

  if (!procedure) {
    return (
      <div className="step-editor">
        <div className="step-editor-error">Procedure not found</div>
      </div>
    );
  }

  return (
    <div className="step-editor">
      <div className="step-editor-header">
        <h3>Edit Steps: {procedure.name}</h3>
        {onClose && (
          <button className="step-editor-close" type="button" onClick={onClose}>✕</button>
        )}
      </div>

      <div className="step-editor-content">
        {steps.length === 0 ? (
          <div className="step-editor-empty">
            <p>No steps defined. Click &quot;Add Step&quot; to create one.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            {steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>

                {/* Step Number Badge */}
                <div style={{ width: '28px', height: '28px', background: '#e0f2fe', color: '#0369a1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem', flexShrink: 0 }}>
                    {i + 1}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => moveStep(i, "up")}
                    disabled={i === 0}
                    style={{ border: 'none', background: 'none', cursor: i === 0 ? 'not-allowed' : 'pointer', opacity: i === 0 ? 0.3 : 1, fontSize: '0.9rem' }}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveStep(i, "down")}
                    disabled={i === steps.length - 1}
                    style={{ border: 'none', background: 'none', cursor: i === steps.length - 1 ? 'not-allowed' : 'pointer', opacity: i === steps.length - 1 ? 0.3 : 1, fontSize: '0.9rem' }}
                    title="Move down"
                  >
                    ↓
                  </button>
                </div>

                <input
                  style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  value={step.label || ""}
                  onChange={(e) => updateStep(i, "label", e.target.value)}
                  placeholder="e.g. Administer LA"
                />

                <select 
                   style={{ width: '140px', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                   value={step.chart_mutation || "NONE"} 
                   onChange={(e) => updateStep(i, "chart_mutation", e.target.value)}
                >
                  <option value="NONE">No Chart UI</option>
                  <option value="FILLING">Filling</option>
                  <option value="CROWN">Crown</option>
                  <option value="RCT_OPEN">RCT Open</option>
                  <option value="RCT_DONE">RCT Done</option>
                  <option value="RCT_CROWN">RCT Crown</option>
                  <option value="EXTRACT">Extract Tooth</option>
                </select>

                <button type="button" onClick={() => removeStep(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <Button type="button" variant="outline" onClick={addStep}>+ Add Next Step</Button>
            <div style={{ display: 'flex', gap: '12px' }}>
                {onClose && <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>}
                <Button type="button" variant="primary" onClick={save}>Save Sequence</Button>
            </div>
        </div>
      </div>
    </div>
  );
}
